<template>
  <div ref="container" class="country-select">
    <div class="country-select__input-wrapper">
      <input
        ref="input"
        v-model="search"
        type="text"
        class="input country-select__input"
        :class="{ error, open: isOpen }"
        :placeholder="$t('registrationPage.form.country') + '*'"
        :disabled="disabled"
        autocomplete="off"
        @focus="open"
        @input="open"
        @keydown.esc="close"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.enter.prevent="selectHighlighted"
      />

      <button
        v-if="search"
        type="button"
        class="country-select__clear"
        aria-label="Clear country"
        @mousedown.prevent="clear"
      >
        <picture>
          <source
            srcset="@/assets/images/close-white.svg"
            media="(prefers-color-scheme: dark)"
          />
          <img src="@/assets/images/close.svg" alt="" />
        </picture>
      </button>
    </div>

    <div v-if="isOpen" class="country-select__dropdown">
      <ul class="country-select__options">
        <li v-if="filteredCountries.length === 0" class="country-select__empty">
          {{ $t("registrationPage.form.noCountriesFound") }}
        </li>

        <li
          v-for="(country, index) in filteredCountries"
          :key="country.code"
          class="country-select__option"
          :class="{
            selected: country.code === modelValue,
            highlighted: index === highlightedIndex
          }"
          @mousedown.prevent="selectCountry(country)"
        >
          {{ getName(country) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { countries, Country } from "@/models/countries"
import { getCountryName } from "@/utils/getCountryName"
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

interface CountrySelectProps {
  modelValue: string
  error?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<CountrySelectProps>(), {
  error: false,
  disabled: false
})

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()

const { locale } = useI18n()

const container = ref<HTMLElement | null>(null)
const input = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const search = ref("")
const highlightedIndex = ref(-1)

const selectedCountry = computed(() =>
  countries.find((country) => country.code === props.modelValue)
)

const getName = (country: Country) => {
  return getCountryName(country, locale.value)
}

const filteredCountries = computed(() => {
  const searchValue = search.value.trim().toLocaleLowerCase()

  if (!searchValue) {
    return countries
  }

  return countries.filter((country) => {
    const name = getName(country).toLocaleLowerCase()

    return (
      name.includes(searchValue) ||
      country.code.toLocaleLowerCase().includes(searchValue)
    )
  })
})

const open = () => {
  if (props.disabled) {
    return
  }

  isOpen.value = true
  highlightedIndex.value = -1
}

const close = () => {
  isOpen.value = false
  highlightedIndex.value = -1

  search.value = selectedCountry.value ? getName(selectedCountry.value) : ""
}

const clear = () => {
  search.value = ""
  isOpen.value = true
  highlightedIndex.value = -1

  emit("update:modelValue", "")

  nextTick(() => {
    input.value?.focus()
  })
}

const selectCountry = (country: Country) => {
  emit("update:modelValue", country.code)
  search.value = getName(country)
  close()
}

const moveDown = () => {
  if (!isOpen.value) {
    open()
    return
  }

  if (filteredCountries.value.length === 0) {
    return
  }

  highlightedIndex.value =
    highlightedIndex.value < filteredCountries.value.length - 1
      ? highlightedIndex.value + 1
      : 0
}

const moveUp = () => {
  if (!isOpen.value) {
    open()
    return
  }

  if (filteredCountries.value.length === 0) {
    return
  }

  highlightedIndex.value =
    highlightedIndex.value > 0
      ? highlightedIndex.value - 1
      : filteredCountries.value.length - 1
}

const selectHighlighted = () => {
  const country = filteredCountries.value[highlightedIndex.value]

  if (country) {
    selectCountry(country)
  }
}

const handleClickOutside = (event: MouseEvent) => {
  if (container.value && !container.value.contains(event.target as Node)) {
    close()
  }
}

watch(
  [selectedCountry, locale],
  ([country]) => {
    if (!isOpen.value) {
      search.value = country ? getName(country) : ""
    }
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener("click", handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside)
})
</script>

<style scoped lang="scss">
.country-select {
  position: relative;
  width: 100%;
  margin-bottom: 0.5rem;
}

.country-select__input-wrapper {
  position: relative;
}

.country-select__input {
  margin-bottom: 0;
}

.country-select__dropdown {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  z-index: 10;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid $input-border;
  border-radius: 0.5rem;
  background-color: $input-background;
  box-shadow: $shadow-card;
}

.country-select__options {
  max-height: 12rem;
  overflow-y: auto;
}

.country-select__option {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 16px;
  cursor: pointer;
}

.country-select__option:hover,
.country-select__option.highlighted,
.country-select__option.selected {
  background-color: $select-background;
  color: $select-text;
}

.country-select__option.selected {
  font-weight: 700;
}

.country-select__empty {
  padding: 0.75rem 1rem;
  color: $color-text-secondary;
  font-size: 14px;
}

.country-select__clear {
  position: absolute;
  top: 55%;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  transform: translateY(-50%);
  cursor: pointer;
}

.country-select__clear picture {
  width: 0.75rem;
}

@media (prefers-color-scheme: dark) {
  .country-select__dropdown {
    border-color: $input-border-dark;
    background-color: $input-background-dark;
  }

  .country-select__option:hover,
  .country-select__option.highlighted,
  .country-select__option.selected {
    background-color: $select-background-dark;
    color: $select-text-dark;
  }

  .country-select__empty {
    color: $color-text-secondary-dark;
  }
}
</style>
